/* ============================================================
   AHA JOYAS — WebGL gold-dust field
   Fine champagne particles drifting over obsidian, additive glow,
   subtle cursor parallax. Lazy, pauses off-screen / hidden tab,
   respects prefers-reduced-motion, degrades to nothing (the CSS
   gradient/image beneath stays intact) on any failure.
   Target: any <canvas data-aha-webgl> on the page.
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var VERT = [
    'attribute vec2 a_pos;',
    'attribute float a_size;',
    'attribute float a_alpha;',
    'uniform vec2 u_res;',
    'uniform vec2 u_parallax;',
    'varying float v_alpha;',
    'void main() {',
    '  vec2 p = a_pos + u_parallax * (a_size * 0.9);',
    '  vec2 clip = (p / u_res) * 2.0 - 1.0;',
    '  clip.y = -clip.y;',
    '  gl_Position = vec4(clip, 0.0, 1.0);',
    '  gl_PointSize = a_size;',
    '  v_alpha = a_alpha;',
    '}'
  ].join('\n');

  var FRAG = [
    'precision mediump float;',
    'varying float v_alpha;',
    'uniform vec3 u_color;',
    'void main() {',
    '  vec2 c = gl_PointCoord - vec2(0.5);',
    '  float d = length(c);',
    '  float core = smoothstep(0.5, 0.0, d);',       // soft disc
    '  float glow = pow(core, 1.15);',               // brighter halo
    '  float spark = pow(core, 6.0) * 0.8;',         // hot center
    '  gl_FragColor = vec4(u_color, (glow + spark) * v_alpha);',
    '}'
  ].join('\n');

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) return null;
    return s;
  }

  function initField(canvas) {
    if (canvas.__ahaInit) return;
    canvas.__ahaInit = true;
    var gl;
    try {
      gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: true })
        || canvas.getContext('experimental-webgl', { alpha: true, premultipliedAlpha: false });
    } catch (e) { return; }
    if (!gl) return;

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;
    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);

    var a_pos = gl.getAttribLocation(prog, 'a_pos');
    var a_size = gl.getAttribLocation(prog, 'a_size');
    var a_alpha = gl.getAttribLocation(prog, 'a_alpha');
    var u_res = gl.getUniformLocation(prog, 'u_res');
    var u_parallax = gl.getUniformLocation(prog, 'u_parallax');
    var u_color = gl.getUniformLocation(prog, 'u_color');

    // Oro AHÁ #D4AF37 normalized
    gl.uniform3f(u_color, 0.831, 0.686, 0.216);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE); // additive
    gl.clearColor(0, 0, 0, 0);

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var W = 0, H = 0;
    var intensity = parseFloat(canvas.getAttribute('data-intensity')) || 1;

    var particles = [];
    var buffer = gl.createBuffer();
    var data; // Float32Array, 4 floats per particle

    function count() {
      var area = (canvas.clientWidth * canvas.clientHeight) / 20000;
      var base = window.innerWidth < 750 ? 150 : 440;
      return Math.max(90, Math.min(700, Math.round(base * intensity * Math.min(1.5, area / 40))));
    }

    function seed() {
      var n = count();
      particles = [];
      for (var i = 0; i < n; i++) {
        var depth = Math.random();
        var big = Math.random() < 0.12;  // a few soft gold bokeh
        particles.push({
          x: Math.random() * W,
          y: Math.random() * H,
          vx: (Math.random() - 0.5) * 0.12 * dpr,
          vy: -(0.08 + Math.random() * 0.34) * dpr * (0.5 + depth),
          size: (big ? 7.0 + depth * 8.0 : 1.6 + depth * 5.2) * dpr,
          base: big ? 0.10 + depth * 0.16 : 0.4 + depth * 0.55,
          phase: Math.random() * Math.PI * 2,
          tw: 0.5 + Math.random() * 1.6
        });
      }
      data = new Float32Array(n * 4);
    }

    function resize() {
      var cw = canvas.clientWidth, ch = canvas.clientHeight;
      if (!cw || !ch) return;
      W = Math.round(cw * dpr);
      H = Math.round(ch * dpr);
      canvas.width = W;
      canvas.height = H;
      gl.viewport(0, 0, W, H);
      gl.uniform2f(u_res, W, H);
      seed();
    }

    var px = 0, py = 0, tpx = 0, tpy = 0;
    function onPointer(e) {
      var r = canvas.getBoundingClientRect();
      tpx = ((e.clientX - r.left) / r.width - 0.5) * 14 * dpr;
      tpy = ((e.clientY - r.top) / r.height - 0.5) * 14 * dpr;
    }

    function frame(t) {
      if (!running) return;
      time = t * 0.001;
      px += (tpx - px) * 0.04;
      py += (tpy - py) * 0.04;
      gl.uniform2f(u_parallax, px, py);

      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < -4) { p.y = H + 4; p.x = Math.random() * W; }
        if (p.x < -4) p.x = W + 4;
        else if (p.x > W + 4) p.x = -4;
        var a = p.base * (0.55 + 0.45 * Math.sin(time * p.tw + p.phase));
        var o = i * 4;
        data[o] = p.x; data[o + 1] = p.y; data[o + 2] = p.size; data[o + 3] = a;
      }

      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);
      var stride = 16;
      gl.enableVertexAttribArray(a_pos);
      gl.vertexAttribPointer(a_pos, 2, gl.FLOAT, false, stride, 0);
      gl.enableVertexAttribArray(a_size);
      gl.vertexAttribPointer(a_size, 1, gl.FLOAT, false, stride, 8);
      gl.enableVertexAttribArray(a_alpha);
      gl.vertexAttribPointer(a_alpha, 1, gl.FLOAT, false, stride, 12);
      gl.drawArrays(gl.POINTS, 0, particles.length);

      raf = requestAnimationFrame(frame);
    }

    var running = false, raf = null, time = 0;
    function start() {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(frame);
    }
    function stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    }

    resize();
    window.addEventListener('resize', debounce(resize, 200));
    window.addEventListener('pointermove', onPointer, { passive: true });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else if (visible) start();
    });

    // Static single frame for reduced motion, then leave it.
    if (reduce) {
      seed();
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i], o = i * 4;
        data[o] = p.x; data[o + 1] = p.y; data[o + 2] = p.size; data[o + 3] = p.base;
      }
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(a_pos); gl.vertexAttribPointer(a_pos, 2, gl.FLOAT, false, 16, 0);
      gl.enableVertexAttribArray(a_size); gl.vertexAttribPointer(a_size, 1, gl.FLOAT, false, 16, 8);
      gl.enableVertexAttribArray(a_alpha); gl.vertexAttribPointer(a_alpha, 1, gl.FLOAT, false, 16, 12);
      gl.drawArrays(gl.POINTS, 0, particles.length);
      return;
    }

    var visible = false;
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible && !document.hidden) start(); else stop();
      }, { threshold: 0.01 });
      io.observe(canvas);
    } else {
      visible = true;
      start();
    }
  }

  function debounce(fn, ms) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  function boot() {
    var canvases = document.querySelectorAll('canvas[data-aha-webgl]');
    for (var i = 0; i < canvases.length; i++) {
      try { initField(canvases[i]); } catch (e) { /* silent: fallback stays */ }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when a section is (re)rendered in the Shopify theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
